import { Card } from '@mui/material';
import { useParams } from 'react-router';
import { checkAclValidation } from 'src/careflow/ph-core/admin/permissions/util/permission.utils';
import ExpandableCard from 'src/components/common/ExpandableCard';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import crsAcls from 'src/careflow/ph-core/admin/permissions/util/crs/crsAcls';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { getRegistries } from 'src/careflow/ph-core/multi-tenant/registry/registry';
import { ConditionWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Condition';
import { useConditions, useObservations } from 'src/careflow/ph-core/fhir-client/fhir-react';
import { ObservationWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Observation';
import PatientGenericServiceRequest, { ServiceRequestsPermissions } from 'src/careflow/care-coordination/patient/service-request/PatientGenericServiceRequest';
import PatientCase from 'src/careflow/care-coordination/patient/cases/patientCase';
import PatientGenericCarePlan from 'src/careflow/care-coordination/patient/care-plan/PatientGenericCarePlan';

export default function PatientReferralCases({ patient }: any) {
  const { i18n } = useLocales();
  const { patientId } = useParams();

  const { data: taskADRegistry } = useQuery({
    queryKey: ['taskActivityDefinition'],
    queryFn: async () => {
      const taskActivityDefinition = await getRegistries(undefined, 'task-activity-definition');
      return taskActivityDefinition;
    },
  });

  const taskActivityDefinition = useMemo(() => 
    taskADRegistry?.[0]?.keyValue ? JSON.parse(taskADRegistry?.[0]?.keyValue) : null
    ,[taskADRegistry]);

  const serviceRequestPermissions: ServiceRequestsPermissions = useMemo(
    () => ({
      isAllowedToAdd: checkAclValidation({ acls: [crsAcls.CRS.PATIENT.SERVICE_REQUESTS.ADD] }),
      isAllowedToEdit: checkAclValidation({ acls: [crsAcls.CRS.PATIENT.SERVICE_REQUESTS.EDIT] }),
      isAllowedToViewNotes: checkAclValidation({
        acls: [crsAcls.CRS.PATIENT.SERVICE_REQUESTS.NOTES.VIEW],
      }),
      taskPermissions: {
        isAllowedToAdd: checkAclValidation({
          acls: [crsAcls.CRS.PATIENT.SERVICE_REQUESTS.TASKS.ADD],
        }),
        isAllowedToEdit: checkAclValidation({
          acls: [crsAcls.CRS.PATIENT.SERVICE_REQUESTS.TASKS.EDIT],
        }),
        notes: {
          isAllowedToView: checkAclValidation({
            acls: [crsAcls.CRS.PATIENT.SERVICE_REQUESTS.TASKS.NOTES.VIEW],
          }),
          isAllowedToAdd: checkAclValidation({
            acls: [crsAcls.CRS.PATIENT.SERVICE_REQUESTS.TASKS.NOTES.ADD],
          }),
        },
        isAllowedToAssignProgram: checkAclValidation({
          acls: [crsAcls.CRS.PATIENT.SERVICE_REQUESTS.TASKS.HANDLER.ASSIGN_PROGRAM],
        }),
        isAllowedReferralInternal: checkAclValidation({
          acls: [crsAcls.CRS.PATIENT.SERVICE_REQUESTS.TASKS.HANDLER.REFERRAL_INTERNAL],
        }),
        isAllowedReferralExternal:checkAclValidation({
          acls: [crsAcls.CRS.PATIENT.SERVICE_REQUESTS.TASKS.HANDLER.REFERRAL_EXTERNAL],
        }),
        isAllowedAppointmentScheduling: checkAclValidation({
          acls: [crsAcls.CRS.PATIENT.SERVICE_REQUESTS.TASKS.HANDLER.APPOINTMENT],
        }),
        isAllowedWarmHandoff: checkAclValidation({
          acls: [crsAcls.CRS.PATIENT.SERVICE_REQUESTS.TASKS.HANDLER.WARM_HANDOFF],
        }),
      }
    }),
    []
  );

  const [observations] = useObservations({
    filter: { patient: patient?.id },
    map: ObservationWrapper,
    autofetch: !!patient?.id,
  });

  const [conditions] = useConditions({
    filter: { patient: patient?.id },
    map: ConditionWrapper,
    autofetch: !!patient?.id,
  });

  return (
    <Card sx={{ py: 2, boxShadow: 'none' }}>
      {checkAclValidation({
        acls: [crsAcls.CRS.PATIENT.SERVICE_REQUESTS.VIEW],
      }) ? (
        <ExpandableCard
          title={i18n('patients.details.serviceRequests.title', 'crs')}
          sxProp={{ m: 1 }}
        >
          <PatientGenericServiceRequest 
            patient={patient}
            permissions={serviceRequestPermissions}
            taskActivityDefinitionContext={taskActivityDefinition?.patientServiceRequest} 
            observations={observations}
            conditions={conditions}
          />
        </ExpandableCard>
      ) : null}
      {checkAclValidation({ acls: [crsAcls.CRS.PATIENT.CASES.VIEW] })}
      <ExpandableCard title={i18n('patients.details.cases.title', 'crs')} sxProp={{ m: 1 }}>
        <PatientCase 
          patientId={patientId}
          title={i18n('patients.details.cases.title', 'crs')}
          i18n={i18n}
        />
      </ExpandableCard>

      {checkAclValidation({
        acls: [crsAcls.CRS.PATIENT.CARE_PLAN.VIEW],
      }) ? (
        <ExpandableCard title={i18n('patients.details.carePlans.title', 'crs')} sxProp={{ m: 1 }}>
          <PatientGenericCarePlan patientId={patientId} i18n={i18n} />
        </ExpandableCard>
      ) : null}
    </Card>
  );
}
